import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import FlagIcon from '@mui/icons-material/Flag';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, GridPagination, GridSortModel } from '@mui/x-data-grid';

import { getRegions } from '@luxuryescapes/lib-regions';
import { ExtendedRegion } from '@luxuryescapes/lib-regions/lib/extended';

import { ChatModal } from '~/components/SupportAssistant/components/ChatModal';

import useAppNavigation from '~/hooks/useAppNavigation';
import useLocalStorage from '~/hooks/useLocalStorage';

import { exportChatsStream } from '~/services/SupportService';
import { formatDateOrdinalWithClock } from '~/services/TimeService';

import userUtils from '~/utils/userUtils';

import { ChatQuality } from '../../types/Chat';

import { useGetChatTopics } from './hooks/useGetChatTopics';
import { useSearchChatsParams } from './hooks/useSearchChatsParams';
import { useSearchChatsQuery } from './hooks/useSearchChatsQuery';
import { useSelectChatById } from './hooks/useSelectChatById';

const MAX_VISIBLE_FILTER_CHIPS = 2;

function renderSelectedFilterChips(selected: unknown) {
  if (!Array.isArray(selected)) {
    return null;
  }

  const visibleValues = selected.slice(0, MAX_VISIBLE_FILTER_CHIPS);
  const hiddenCount = selected.length - visibleValues.length;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
      {visibleValues.map((value) => (
        <Chip key={value} label={value} size="small" />
      ))}
      {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} size="small" />}
    </Box>
  );
}

const columns: Array<GridColDef> = [
  {
    field: 'title',
    headerName: 'Title',
    disableColumnMenu: true,
    minWidth: 100,
    flex: 1,
    display: 'flex',
  },
  {
    field: 'status',
    headerName: 'Status',
    disableColumnMenu: true,
    minWidth: 50,
    flex: 0,
    cellClassName: (params: GridCellParams) =>
      clsx('chat', {
        active: params.row.status === 'ACTIVE',
      }),
    display: 'flex',
  },
  {
    field: 'quality',
    headerName: 'Quality',
    disableColumnMenu: true,
    minWidth: 50,
    flex: 0,
    cellClassName: (params: GridCellParams) =>
      clsx('chat', {
        ['low-quality']: params.row.quality === ChatQuality.LOW,
        ['high-quality']: params.row.quality === ChatQuality.HIGH,
      }),
    display: 'flex',
  },
  {
    field: 'followUp',
    headerName: 'Follow Up',
    disableColumnMenu: true,
    minWidth: 50,
    flex: 0,
    cellClassName: (params: GridCellParams) =>
      clsx('chat', {
        active: params.row.followUp?.status === 'ACTIVE',
      }),
    renderCell: (params: GridCellParams) => (
      <>
        {params.value && (
          <Stack direction="row" alignItems="center">
            {params.row.followUp?.team}
            <Tooltip title={params.row.followUp?.message}>
              <IconButton size="small" color="info">
                {params.row.followUp?.status === 'ACTIVE' ? (
                  <QuestionMarkIcon fontSize="inherit" />
                ) : (
                  <CheckIcon fontSize="inherit" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </>
    ),
    display: 'flex',
  },
  {
    field: 'regionCode',
    headerName: 'Region',
    renderCell: (params: GridCellParams) => <Typography variant="body2">{params.row.regionCode}</Typography>,
  },
  {
    field: 'assistantVersion',
    headerName: 'Assistant',
    disableColumnMenu: true,
    minWidth: 90,
    flex: 0,
    display: 'flex',
  },
  {
    field: 'customerId',
    renderCell: (params: GridCellParams) => (
      <>
        {params.value && (
          <Link
            target="_blank"
            color={userUtils.isEmployeeEmail(params.row.customer?.email) ? '#ff0000' : '#000'}
            href={`/users/${params.row.customer?.id_member}`}
          >
            <Typography variant="body2">{params.row.customer?.full_name}</Typography>
            <Typography variant="body2">{params.row.customer?.email}</Typography>
          </Link>
        )}
      </>
    ),
    headerName: 'User',
    disableColumnMenu: true,
    sortable: false,
    minWidth: 150,
    flex: 1,
    display: 'flex',
  },
  {
    field: 'topics',
    headerName: 'Topics',
    disableColumnMenu: true,
    minWidth: 150,
    flex: 1,
    display: 'flex',
  },
  {
    field: 'createdAt',
    valueFormatter: (value) => formatDateOrdinalWithClock(new Date(value as string)),
    headerName: 'Created at',
    disableColumnMenu: true,
    minWidth: 75,
    flex: 1,
    display: 'flex',
  },
  {
    field: 'updatedAt',
    valueFormatter: (value) => formatDateOrdinalWithClock(new Date(value as string)),
    headerName: 'Updated at',
    disableColumnMenu: true,
    minWidth: 75,
    flex: 1,
    display: 'flex',
  },
  {
    field: 'hasDeadEnd',
    headerName: 'Dead end',
    disableColumnMenu: true,
    minWidth: 80,
    flex: 1,
    renderCell: (params: GridCellParams) => (params.value ? <FlagIcon color="error" /> : ''),
    display: 'flex',
  },
  {
    field: 'agentType',
    headerName: 'Agent type',
    disableColumnMenu: true,
    minWidth: 80,
    flex: 1,
    cellClassName: (params: GridCellParams) =>
      clsx('chat', {
        agent: params.row.agentType === 'AGENT',
      }),
    display: 'flex',
  },
];

export function ChatHistoryPage() {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const regions: Array<ExtendedRegion> = useMemo(() => {
    const priorityCodes = ['AU', 'US', 'GB', 'NZ', 'SG'];
    const allRegions = getRegions(brand);
    const priorityRegions: Array<ExtendedRegion> = [];
    const otherRegions: Array<ExtendedRegion> = [];

    allRegions.forEach((region) => {
      if (priorityCodes.includes(region.code)) {
        priorityRegions.push(region);
      } else {
        otherRegions.push(region);
      }
    });

    // Sort priority regions in the order of priorityCodes
    priorityRegions.sort((a, b) => priorityCodes.indexOf(a.code) - priorityCodes.indexOf(b.code));

    // Sort other regions alphabetically by code
    otherRegions.sort((a, b) => a.code.localeCompare(b.code));

    return [...priorityRegions, ...otherRegions];
  }, [brand]);
  const [messagesModalOpen, setMessagesModalOpen] = useState(false);
  const [chatExportInProgress, setChatExportInProgress, clearChatExportInProgress] = useLocalStorage(
    'chatExportInProgress',
    false,
  );
  const [exporting, setExporting] = useState(chatExportInProgress);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const location = useLocation();
  const { push, replace } = useAppNavigation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const searchQuery = params.get('search');
  const selectedChatIdQuery = params.get('chat');
  const pageQuery = parseInt(params.get('page'));

  const [queryString, setQueryString] = useState<string | undefined>(searchQuery || undefined);

  const {
    setters: {
      setFollowUpOnly,
      setAgentChatsOnly,
      setChatTopicsFilter,
      setChatRegionsFilter,
      setCustomersOnly,
      setFilterByDeadEnd,
      setHasMessagesOnly,
      setV2AssistantOnly,
      setPage,
      setSearchString,
      setSortModel,
    },
    searchParams,
  } = useSearchChatsParams({ searchString: queryString });

  const {
    chats,
    chatsWithCustomerDetails,
    total,
    searchChatsQuery,
    loading: loadingSearchChats,
  } = useSearchChatsQuery();

  const { setSelectedChatId, selectedChat, loading: loadingFetchChatById } = useSelectChatById();

  const { chatTopics: allChatTopics, loading: loadingAllChatTopics } = useGetChatTopics();

  const loading = loadingSearchChats || loadingFetchChatById || loadingAllChatTopics;

  const messagesModalClose = useCallback(() => {
    if (selectedChatIdQuery) {
      params.delete('chat');
      replace({
        search: params.toString(),
      });
    }
    setSelectedChatId(null);
    searchChatsQuery(searchParams);
  }, [params, replace, searchChatsQuery, searchParams, selectedChatIdQuery, setSelectedChatId]);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    // sortModel will always have 1 item because non-PRO datagrid has only 1 sort at the same time
    setSortModel({
      field: sortModel?.[0]?.field,
      sort: sortModel?.[0]?.sort,
    });
  };

  const onRowClickHandler = useCallback(
    (chat) => {
      setSelectedChatId(chat?.id);
      setMessagesModalOpen(true);
      params.set('chat', chat?.id);
      replace({
        search: params.toString(),
      });
    },
    [params, replace, setSelectedChatId],
  );

  const onCellClickHandler = (params) => {
    if (params.field !== 'customerId' || params.row.customerId === null) {
      onRowClickHandler(params.row);
    }
  };

  const onPageChange = useCallback(
    (page) => {
      if (!isNaN(page)) {
        params.set('page', page);
        replace({
          search: params.toString(),
        });
      }
    },
    [params, replace],
  );

  const updateQuery = useCallback(
    (e) => {
      if (e.keyCode !== 13 && e.target.name !== 'chat-search-submit') {
        return;
      }

      params.set('search', queryString);
      push({
        search: params.toString(),
      });
    },
    [params, push, queryString],
  );

  const handleTopicFilterChange = useCallback(
    (event: SelectChangeEvent<typeof searchParams.chatTopicsFilter>) => {
      const { target } = event;
      setChatTopicsFilter(typeof target.value === 'string' ? target.value.split(',') : target.value);
    },
    [searchParams, setChatTopicsFilter],
  );

  const handleRegionFilterChange = useCallback(
    (event: SelectChangeEvent<typeof searchParams.chatRegionsFilter>) => {
      const { target } = event;
      setChatRegionsFilter(typeof target.value === 'string' ? target.value.split(',') : target.value);
    },
    [searchParams, setChatRegionsFilter],
  );

  useEffect(() => {
    if (!isNaN(pageQuery)) {
      setPage(pageQuery);
    }
  }, [pageQuery, setPage]);

  useEffect(() => {
    setSearchString(searchQuery);
  }, [searchQuery, setSearchString]);

  useEffect(() => {
    const chat = chats.find((chat) => chat.id === selectedChatIdQuery);
    setSelectedChatId(chat?.id || selectedChatIdQuery);
    setMessagesModalOpen(!!(chat?.id || selectedChatIdQuery));
  }, [chats, setSelectedChatId, selectedChatIdQuery]);

  useEffect(() => {
    searchChatsQuery(searchParams);
  }, [searchChatsQuery, searchParams]);

  const onSearchStringChange = useCallback(
    (e: { target: { value: string } }) => {
      setQueryString(e.target.value);
    },
    [setQueryString],
  );

  useEffect(() => {
    setExporting(chatExportInProgress);
  }, [chatExportInProgress]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setChatExportInProgress(true);

    setExportDialogOpen(false);
    enqueueSnackbar('Export started. Download will begin shortly...', { variant: 'info' });

    try {
      const exportParams: any = {};

      if (exportDateFrom) {
        exportParams.from = exportDateFrom;
      }
      if (exportDateTo) {
        exportParams.to = exportDateTo;
      }

      if (searchParams.searchString) {
        exportParams.searchString = searchParams.searchString;
      }
      if (searchParams.filterByDeadEnd) {
        exportParams.hasDeadEnd = 'true';
      }
      if (searchParams.chatTopicsFilter.length > 0) {
        exportParams.chatTopics = searchParams.chatTopicsFilter;
      }
      if (searchParams.chatRegionsFilter.length > 0) {
        exportParams.regionCodes = searchParams.chatRegionsFilter;
      }
      if (searchParams.customersOnly) {
        exportParams.customersOnly = 'true';
      }
      if (searchParams.agentChatsOnly) {
        exportParams.agentChatsOnly = 'true';
      }
      if (searchParams.v2AssistantOnly) {
        exportParams.assistantVersion = 'V2';
      }
      if (searchParams.followUpOnly) {
        exportParams.followUpOnly = 'true';
      }
      if (searchParams.hasMessagesOnly) {
        exportParams.hasMessagesOnly = 'true';
      }

      const response = await exportChatsStream(exportParams);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Export failed');
      }

      const csvContent = await response.text();
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `support-assistant-chats-${timestamp}.csv`;
      fileDownload(csvContent, filename);

      enqueueSnackbar('Export completed successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setExporting(false);
      clearChatExportInProgress();
    }
  }, [enqueueSnackbar, exportDateFrom, exportDateTo, searchParams, setChatExportInProgress, clearChatExportInProgress]);

  return (
    <Box>
      <Stack direction="column" spacing={1}>
        <Typography variant="h4">Search chat history</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            variant="outlined"
            id="ChatSearch"
            data-testid="ChatSearch"
            type="text"
            label="Search query"
            placeholder="Chat ID / Customer ID / Chat Title"
            onChange={onSearchStringChange}
            value={queryString}
            fullWidth
          />
          <Button variant="contained" color="primary" onClick={updateQuery} name="chat-search-submit">
            Search
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setExportDialogOpen(true)}
            disabled={exporting}
            sx={{ minWidth: 100 }}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </Stack>
        <Stack direction="row" alignItems="center">
          <FormControl sx={{ width: 500, mr: 2 }}>
            <InputLabel id="select-chat-topic-label">Chat Topics</InputLabel>
            <Select
              labelId="select-chat-topic-label"
              multiple
              value={searchParams.chatTopicsFilter}
              onChange={handleTopicFilterChange}
              input={<OutlinedInput label="Chat Topics" />}
              renderValue={renderSelectedFilterChips}
            >
              {allChatTopics.map((topic) => (
                <MenuItem
                  key={topic}
                  value={topic}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: 'primary.main',
                    },
                  }}
                >
                  <Checkbox
                    checked={searchParams.chatTopicsFilter.includes(topic)}
                    sx={{
                      color: 'inherit',
                      '&.Mui-checked': {
                        color: 'inherit',
                      },
                    }}
                  />
                  {topic}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: 300, mr: 2 }}>
            <InputLabel id="select-chat-region-label">Regions</InputLabel>
            <Select
              labelId="select-chat-region-label"
              multiple
              value={searchParams.chatRegionsFilter}
              onChange={handleRegionFilterChange}
              input={<OutlinedInput label="Regions" />}
              renderValue={renderSelectedFilterChips}
            >
              {regions.map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.code} - ({region.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.filterByDeadEnd}
                onChange={(event) => setFilterByDeadEnd(event.target.checked)}
              />
            }
            label="Show only dead end chats"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.customersOnly}
                onChange={(event) => setCustomersOnly(event.target.checked)}
              />
            }
            label="Customers only"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.agentChatsOnly}
                onChange={(event) => setAgentChatsOnly(event.target.checked)}
              />
            }
            label="Agent chats"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.v2AssistantOnly}
                onChange={(event) => setV2AssistantOnly(event.target.checked)}
              />
            }
            label="V2 assistant only"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.followUpOnly}
                onChange={(event) => setFollowUpOnly(event.target.checked)}
              />
            }
            label="Follow up only"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.hasMessagesOnly}
                onChange={(event) => setHasMessagesOnly(event.target.checked)}
              />
            }
            label="Hide Empty Chats"
          />
        </Stack>

        {/* Export Progress Indicator */}
        {exporting && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <LinearProgress sx={{ flex: 1 }} />
              <Typography variant="body2" color="primary">
                Exporting chat data...
              </Typography>
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            '& .chat.active': {
              color: (theme) => theme.palette.success.light,
              fontWeight: '600',
            },
            '& .chat.low-quality': {
              color: (theme) => theme.palette.error.light,
              fontWeight: '600',
            },
            '& .chat.high-quality': {
              color: (theme) => theme.palette.success.light,
              fontWeight: '600',
            },
            '& .chat.agent': {
              color: (theme) => theme.palette.success.light,
              fontWeight: '600',
            },
          }}
        >
          <DataGrid
            autoHeight
            columns={columns}
            getRowHeight={() => 'auto'}
            density="compact"
            loading={loading}
            onPaginationModelChange={({ page }) => onPageChange(page)}
            onSortModelChange={handleSortModelChange}
            onCellClick={(params) => onCellClickHandler(params)}
            pageSizeOptions={[searchParams.limit]}
            paginationMode="server"
            paginationModel={{ pageSize: searchParams.limit, page: searchParams.page }}
            rowCount={total}
            rows={chatsWithCustomerDetails || []}
            slots={{
              loadingOverlay: () => <LinearProgress color="primary" />,
              pagination: GridPagination,
            }}
            sortingMode="server"
            sortModel={[searchParams.sortModel]}
          />
        </Box>
      </Stack>
      <ChatModal
        open={messagesModalOpen}
        onClose={messagesModalClose}
        loading={loading}
        chat={selectedChat}
        allChatTopics={allChatTopics}
      />

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Chat Data</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Export chat data with optional date filtering. Leave dates empty to export all available data.
            </Typography>

            <Stack direction="row" spacing={2}>
              <TextField
                label="From Date"
                type="date"
                value={exportDateFrom}
                onChange={(e) => setExportDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="To Date"
                type="date"
                value={exportDateTo}
                onChange={(e) => setExportDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Export will include all chats from the database matching your filters (no limit).
            {exportDateFrom || exportDateTo ? ' (filtered by date range)' : ''}
          </Typography>
          <Typography variant="body2" color="info.main" sx={{ fontStyle: 'italic' }}>
            Note: Large exports are processed using streaming to prevent memory issues. The dialog will close and
            download will start automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={exporting || !chatsWithCustomerDetails || chatsWithCustomerDetails.length === 0}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChatHistoryPage;
